import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MobileFeedScreen, MobileVideosScreen } from "@/src/common/components/app-mock/mobile-screens";
import { MOCK_COMMUNITY } from "@/src/common/components/app-mock/mock-community";
import { CalendarWidget } from "@/src/common/components/app-mock/web-screens/calendar-widget";
import { WebAiInsightsScreen } from "@/src/common/components/app-mock/web-screens/web-ai-screen";
import { WebAnalyticsScreen } from "@/src/common/components/app-mock/web-screens/web-analytics-screen";
import { WebCommunitySettingsScreen } from "@/src/common/components/app-mock/web-screens/web-community-settings-screen";
import { WebEventsScreen } from "@/src/common/components/app-mock/web-screens/web-events-screen";
import { WebFeedScreen } from "@/src/common/components/app-mock/web-screens/web-feed-screen";
import { WebMatchScreen } from "@/src/common/components/app-mock/web-screens/web-match-screen";
import { WebMeetingsScreen } from "@/src/common/components/app-mock/web-screens/web-meetings-screen";
import { WebMembersScreen } from "@/src/common/components/app-mock/web-screens/web-members-screen";
import { WebMessagesScreen } from "@/src/common/components/app-mock/web-screens/web-messages-screen";
import { WEB_NAV_PATH, WebAppShell, type WebNavId } from "@/src/common/components/app-mock/web-shell";
import { BrowserFrame } from "@/src/common/components/browser-frame";
import { PhoneFrame } from "@/src/common/components/phone-frame";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

export type FeatureVisualKind =
	| "feed"
	| "messaging"
	| "events"
	| "meetings"
	| "clips"
	| "match"
	| "insights"
	| "members"
	| "analytics"
	| "branded"
	| "theming";

interface Props {
	kind: FeatureVisualKind;
}

interface WebCropProps {
	nav: WebNavId;
	label: string;
	tabTitle: string;
	children: ReactNode;
	fullBleed?: boolean;
	decorative?: boolean;
	height?: string;
}

function WebCrop({ nav, label, tabTitle, children, fullBleed, decorative = true, height = "h-[420px]" }: WebCropProps) {
	const { palette } = useCommunityVariant();
	return (
		<BrowserFrame
			url={`${MOCK_COMMUNITY.domain}${WEB_NAV_PATH[nav]}`}
			label={label}
			tabTitle={tabTitle}
			faviconColor={palette.accent}
			decorative={decorative}
		>
			<div className={`${height} overflow-hidden`}>
				<WebAppShell activeNav={nav} layout="compact" fullBleed={fullBleed} scroll={false}>
					{children}
				</WebAppShell>
			</div>
		</BrowserFrame>
	);
}

function PhoneCrop({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="flex justify-center py-4">
			<PhoneFrame size="sm" label={label}>
				{children}
			</PhoneFrame>
		</div>
	);
}

export function FeatureVisual({ kind }: Props) {
	const { t } = useTranslation("features");
	const { palette } = useCommunityVariant();

	switch (kind) {
		case "feed":
			return (
				<WebCrop nav="feed" label={t("frames.feed")} tabTitle="Home">
					<WebFeedScreen maxPosts={2} banner={false} />
				</WebCrop>
			);
		case "messaging":
			return (
				<WebCrop nav="messages" label={t("frames.messaging")} tabTitle="Messages" fullBleed>
					<WebMessagesScreen compact />
				</WebCrop>
			);
		case "events":
			return (
				<WebCrop nav="events" label={t("frames.events")} tabTitle="Events">
					<WebEventsScreen sidebar={false} maxEvents={3} />
				</WebCrop>
			);
		case "meetings":
			return (
				<WebCrop nav="meetings" label={t("frames.meetings")} tabTitle="Meetings" height="h-[480px]">
					<div className="flex flex-col gap-3">
						<CalendarWidget legend />
						<WebMeetingsScreen sidebar={false} maxMeetings={3} />
					</div>
				</WebCrop>
			);
		case "clips":
			return (
				<PhoneCrop label={t("frames.clips")}>
					<MobileVideosScreen />
				</PhoneCrop>
			);
		case "match":
			return (
				<WebCrop nav="members" label={t("frames.match")} tabTitle="Match AI">
					<WebMatchScreen maxMatches={3} />
				</WebCrop>
			);
		case "insights":
			return (
				<WebCrop nav="admin-ai" label={t("frames.insights")} tabTitle="AI Insights" fullBleed>
					<WebAiInsightsScreen promptChips={false} />
				</WebCrop>
			);
		case "members":
			return (
				<WebCrop nav="members" label={t("frames.members")} tabTitle="Members">
					<WebMembersScreen maxRows={6} />
				</WebCrop>
			);
		case "analytics":
			return (
				<WebCrop nav="admin-analytics" label={t("frames.analytics")} tabTitle="Analytics" height="h-[440px]">
					<WebAnalyticsScreen rows={["metrics", "chart"]} />
				</WebCrop>
			);
		case "branded":
			return (
				<PhoneCrop label={t("frames.branded", { theme: palette.label })}>
					<MobileFeedScreen />
				</PhoneCrop>
			);
		case "theming":
			return (
				<WebCrop nav="admin-community" label={t("frames.theming")} tabTitle="Community" decorative={false}>
					<WebCommunitySettingsScreen interactive sections={["appearance", "identity"]} />
				</WebCrop>
			);
	}
}
